import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

type ChartType = 'pie' | 'rectangle';

interface SubLevel {
  chartType: ChartType;
  numerator: number;
  denominator: number;
  instruction?: string;
}

interface LevelData {
  shuffle: boolean;
  subLevels: SubLevel[];
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: LevelData[];
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;

export function mountFractionsCreate(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const subLevels = buildSubLevels(level);
  let subIndex = 0;
  let cfg = subLevels[0]!;
  let filled: boolean[] = [];
  let correctCount = 0;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Crie a fração</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage fractions-create">
      <div class="feedback" aria-live="polite"></div>
      <p class="fractions-create__progress" aria-live="polite"></p>
      <p class="fractions-create__hint"></p>
      <div class="fractions-create__chart-wrap"></div>
      <p class="fractions-create__count" aria-live="polite"></p>
      <button class="btn btn--primary fractions-create__ok" type="button">Confirmar</button>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.fractions-create__progress')!;
  const hintEl = page.querySelector<HTMLElement>('.fractions-create__hint')!;
  const chartWrap = page.querySelector<HTMLElement>('.fractions-create__chart-wrap')!;
  const countEl = page.querySelector<HTMLElement>('.fractions-create__count')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.fractions-create__ok')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  function startRound(): void {
    cfg = subLevels[subIndex]!;
    filled = new Array(cfg.denominator).fill(false);
    renderProgress();
    renderHint();
    renderChart();
    renderCount();
  }

  function renderProgress(): void {
    progressEl.textContent = `Questão ${subIndex + 1} de ${subLevels.length}`;
  }

  function renderHint(): void {
    const shape = cfg.chartType === 'pie' ? 'pizza' : 'quadrado';
    hintEl.innerHTML = `Pinte <strong>${cfg.numerator}/${cfg.denominator}</strong> da ${shape}.`;
  }

  function renderChart(): void {
    chartWrap.innerHTML = '';
    if (cfg.chartType === 'pie') chartWrap.appendChild(renderPie());
    else chartWrap.appendChild(renderRectangle());
  }

  function renderCount(): void {
    const n = filled.filter(Boolean).length;
    countEl.textContent = `Pintados: ${n} / ${cfg.denominator}`;
  }

  function togglePart(idx: number): void {
    filled[idx] = !filled[idx];
    ctx.audio.play('click');
    renderChart();
    renderCount();
  }

  function renderPie(): SVGElement {
    const size = 220;
    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 6;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
    svg.setAttribute('class', 'fractions-create__chart fractions-create__chart--pie');
    for (let i = 0; i < cfg.denominator; i++) {
      const startAngle = (i * 2 * Math.PI) / cfg.denominator - Math.PI / 2;
      const endAngle = ((i + 1) * 2 * Math.PI) / cfg.denominator - Math.PI / 2;
      const x1 = cx + r * Math.cos(startAngle);
      const y1 = cy + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle);
      const y2 = cy + r * Math.sin(endAngle);
      const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      path.setAttribute('d', d);
      path.setAttribute('class', filled[i] ? 'fractions-create__slice fractions-create__slice--filled' : 'fractions-create__slice');
      path.setAttribute('data-idx', String(i));
      path.addEventListener('click', () => togglePart(i));
      svg.appendChild(path);
    }
    return svg;
  }

  function renderRectangle(): SVGElement {
    const W = 360;
    const H = 120;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.setAttribute('class', 'fractions-create__chart fractions-create__chart--rect');
    const partW = W / cfg.denominator;
    for (let i = 0; i < cfg.denominator; i++) {
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(i * partW));
      rect.setAttribute('y', '0');
      rect.setAttribute('width', String(partW));
      rect.setAttribute('height', String(H));
      rect.setAttribute('class', filled[i] ? 'fractions-create__cell fractions-create__cell--filled' : 'fractions-create__cell');
      rect.setAttribute('data-idx', String(i));
      rect.addEventListener('click', () => togglePart(i));
      svg.appendChild(rect);
    }
    return svg;
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function validate(): void {
    const n = filled.filter(Boolean).length;
    if (n === cfg.numerator) {
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      subIndex++;
      window.setTimeout(() => {
        if (subIndex < subLevels.length) startRound();
        else finishLevel();
      }, 700);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
    }
  }

  okBtn.addEventListener('click', validate);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      validate();
      e.preventDefault();
    }
  };
  window.addEventListener('keydown', onKey);

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você criou ${correctCount} de ${subLevels.length} frações.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/fractions_create/${level.level + 1}`;
      });
    }
  }

  startRound();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}

function buildSubLevels(level: Level): SubLevel[] {
  const result: SubLevel[] = [];
  level.data.forEach((d) => {
    const list = d.shuffle ? shuffle([...d.subLevels]) : d.subLevels;
    for (const s of list) {
      // Filtra impróprias (numerator > denominator) — não suportadas (precisariam múltiplas formas)
      if (s.numerator > s.denominator) continue;
      result.push(s);
    }
  });
  return result;
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}
